import { useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw } from "lucide-react";
import { errorMessage, syncCampaign, type CampaignDetail, type SyncResult } from "@/api/email";
import { Button } from "@/components/ui/button";
import { syncHasMore, syncSummary } from "@/lib/email";

const MAX_ROUNDS = 25;

function add(total: SyncResult | null, next: SyncResult): SyncResult {
  if (!total) return next;
  return {
    ...next,
    campaign_created: total.campaign_created || next.campaign_created,
    steps_created: total.steps_created + next.steps_created,
    events_ingested: total.events_ingested + next.events_ingested,
    contacts_updated: total.contacts_updated + next.contacts_updated,
    contacts_pushed: total.contacts_pushed + next.contacts_pushed,
    contacts_failed: total.contacts_failed + next.contacts_failed,
    contacts_held: total.contacts_held + next.contacts_held,
    errors: [...total.errors, ...next.errors],
    notes: next.notes,
  };
}

/**
 * Sync / Refresh: the campaign and its steps at Lemlist, its events, and
 * the leads Lemlist already holds (their next emails, new steps) — in
 * batches while there is progress. It never hands a new lead over: that is
 * the Launch tab's, and only its.
 */
export function useCampaignSync(campaign: CampaignDetail, external: boolean, onDone: () => void) {
  const [running, setRunning] = useState(false);

  async function run() {
    setRunning(true);
    const toastId = toast.loading(external ? "Syncing with Lemlist…" : "Dry run…");
    let total: SyncResult | null = null;
    try {
      for (let round = 0; round < MAX_ROUNDS; round++) {
        const result = await syncCampaign(campaign.project_slug, campaign.id);
        total = add(total, result);
        if (!syncHasMore(result)) break;
        toast.loading(`${syncSummary(total)} Continuing…`, { id: toastId });
      }
      if (total) {
        toast.success(syncSummary(total), {
          id: toastId,
          description: total.notes.join(" ") || undefined,
        });
        if (total.errors.length) {
          toast.warning(`${total.errors.length} ${total.errors.length === 1 ? "problem" : "problems"}`, {
            description: total.errors.slice(0, 3).join(" · "),
          });
        }
      }
    } catch (err) {
      toast.error(errorMessage(err, "The sync failed."), { id: toastId });
    } finally {
      setRunning(false);
      onDone();
    }
  }

  return { run, running };
}

export function SyncButton({
  running,
  onClick,
  disabled,
  external,
}: {
  running: boolean;
  onClick: () => void;
  disabled?: boolean;
  external: boolean;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={running || disabled}
      title={
        external
          ? "Sync with Lemlist: campaign, events and the leads it holds. Launching new leads is in the Launch tab."
          : "Dry run: simulate the sync"
      }
    >
      {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
      {external ? "Sync" : "Sync (dry run)"}
    </Button>
  );
}
